import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Typography
} from "@mui/material"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import { FranchiseBriefResponse } from "../../infrastructure/models/transport/response/franchiseBrief"
import { UpdateSupportUserRequest } from "../../infrastructure/models/transport/request/UpdateSupportUserRequest"

interface UpdateSupportUserFormProps {
    defaultValues: {
        email: string
        firstname: string
        lastname: string
        role: number
        franchises: { franchiseId: string, name: string }[]
        isActive: boolean
        address: {
            street: string
            city: string
            state: string
            postalCode: string
        }
    }
    onSubmit: (data: UpdateSupportUserRequest) => void
    onCancel: () => void
    isLoading: boolean
}

const UpdateSupportUserForm: React.FC<UpdateSupportUserFormProps> = ({
    defaultValues,
    onSubmit,
    onCancel,
    isLoading,
}) => {
    const [franchiseOptions, setFranchiseOptions] = useState<{
        id: string
        name: string
    }[]>([])
    const sessionService = container.resolve<SessionService>("sessionService")

    const getVisibleRoles = (role: SupportRole): [string, number][] => {
        let allowedRoles: SupportRole[] = []

        switch (role) {
            case SupportRole.SUPERADMIN:
                allowedRoles = [
                    SupportRole.SUPERADMIN,
                    SupportRole.MANAGER,
                    SupportRole.ADMIN,
                    SupportRole.SUPPORT,
                ]
                break
            case SupportRole.MANAGER:
                allowedRoles = [SupportRole.SUPPORT]
                break
            case SupportRole.ADMIN:
                allowedRoles = [
                    SupportRole.MANAGER,
                    SupportRole.SUPPORT,
                ]
                break
            default:
                allowedRoles = []
        }

        return (Object.entries(SupportRole) as [string, number][])
            .filter(([key]) => isNaN(Number(key)))
            .filter(([_, val]) => allowedRoles.includes(val as SupportRole))
    }

    const [formData, setFormData] = useState<UpdateSupportUserRequest>({
        firstname: defaultValues.firstname,
        lastname: defaultValues.lastname,
        email: defaultValues.email,
        role: defaultValues.role,
        franchises: defaultValues.franchises.map((f) => f.franchiseId),
        isActive: defaultValues.isActive,
        address: defaultValues.address,
    })

    useEffect(() => {
        const brief: FranchiseBriefResponse[] = sessionService.franchiseBrief
        const opts =
            brief.length > 0
                ? brief.map((b) => ({ id: b.franchiseId, name: b.name }))
                : sessionService.franchises.map((f) => ({
                    id: f.franchiseId,
                    name: f.name,
                }))
        setFranchiseOptions(opts)
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target

        if (name === "role") {
            setFormData((p) => ({ ...p, role: Number(value) as SupportRole }))
        } else if (name.startsWith("address.")) {
            const key = name.split(".")[1] as keyof typeof formData.address
            setFormData((p) => ({
                ...p,
                address: { ...p.address, [key]: value },
            }))
        } else if (name === "isActive") {
            setFormData((p) => ({ ...p, isActive: value === "true" }))
        } else {
            setFormData((p) => ({ ...p, [name]: value }))
        }
    }

    const toggleFranchise = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            franchises: prev.franchises.includes(id)
                ? prev.franchises.filter((fid) => fid !== id)
                : [...prev.franchises, id],
        }))
    }

    const handleSubmit = () => {
        onSubmit(formData)
    }

    return (
        <Box
            sx={{
                p: 4,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
            }}
        >
            <Typography variant="h5" gutterBottom>
                Update Support User
            </Typography>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        select
                        fullWidth
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        {getVisibleRoles(
                            sessionService.user?.role ?? SupportRole.SUPERADMIN
                        ).map(([label, val]) => (
                            <MenuItem key={val} value={val}>
                                {label.replace(/_/g, " ")}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12 }}>
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            borderRadius: 2,
                            p: 2,
                            position: "relative",
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                position: "absolute",
                                top: "-12px",
                                left: "16px",
                                backgroundColor: "#fff",
                                px: 1,
                            }}
                        >
                            Select Franchises
                        </Typography>
                        <Grid container spacing={1}>
                            {franchiseOptions.map((fr) => (
                                <Grid key={fr.id} size={{ xs: 12, sm: 6 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.franchises.includes(fr.id)}
                                                onChange={() => toggleFranchise(fr.id)}
                                            />
                                        }
                                        label={fr.name}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {/* Address */}
                <Grid size={{ xs: 12, sm: 12 }}>
                    <TextField
                        fullWidth
                        label="Street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                        fullWidth
                        label="City"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                        fullWidth
                        label="State"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                        fullWidth
                        label="Postal Code"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        inputProps={{ maxLength: 6 }}
                    />
                </Grid>

                {/* Status */}
                <Grid size={{ xs: 12, sm: 12 }}>
                    <TextField
                        select
                        fullWidth
                        label="Status"
                        name="isActive"
                        value={formData.isActive.toString()}
                        onChange={handleChange}
                    >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </TextField>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button variant="outlined" color="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? "Updating..." : "Update"}
                </Button>
            </Box>
        </Box>
    )
}

export default UpdateSupportUserForm